import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Divider } from '@mui/material';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { accountDiscoveryActions, accountDiscoveryTypes } from 'app-components/flux/account-discovery';
import type { Account } from 'app-components/flux/accounts';
import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { actionHelpers } from 'app-components/flux/core';
import { houseStatsActions, houseStatsSelectors } from 'app-components/flux/house-stats';
import { createZillowAccount } from 'app-components/flux/house-stats/houseStatsActions';
import type { ZillowSubmittingObject } from 'app-components/flux/house-stats/houseStatsTypes';
import {
  describeProperty,
  getPropertyNameFirstPart,
  getPropertyNameSecondPart,
} from 'app-components/flux/house-stats/houseStatsUtils';
import { rootUiActions } from 'app-components/flux/root-ui';
import { useLoadStates } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';
import { isDemoMode } from 'app-components/utils/core';

import StdDialog from 'components/Dialogs/StdDialog';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import QAmountField from 'components/QAmountField';
import UsageSelect from 'components/UsageSelect';
import { useAppSelector } from 'hooks/redux';
import type { ChangeEvent } from 'react';
import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import type { HousingSelectionType } from 'app-components/flux/housing-selection/housingSelectionTypes';
import AddressSearchField from 'components/AddressSearchField';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import QIllustration from 'components/QIllustration';
import AccountLink from 'components/QuickenControls/AccountField/AccountLink';
import ZeroStateView from 'components/ZeroStateView';
import { v4 as uuidv4 } from 'uuid';
import LoadingInfo from '../LoadingInfo';
import { DIALOG_TYPE_ASSET } from '../actions';
import { PropertyDialogNavigationButton, ZillowImage, ZillowTerms } from './utils';
import usePrivacyMaskValue from 'app-components/hooks/privacyMode/usePrivacyMaskValue';

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'relative',
    minHeight: 410,
  },
  accountName: {
    width: 550,
    marginBottom: '24px',
  },
  estimatedValue: {
    marginBottom: '16px',
  },
  headlineRoot: {
    padding: '24px 30px 10px 150px',
    display: 'grid',
    gap: 12,
  },
  headlineIcon: {
    position: 'absolute',
    height: 135,
    top: 30,
    left: 18,
  },
  errorTab: {
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  houseStatsRoot: {
    position: 'relative',
    height: 450,
    overflowY: 'hidden',
  },
  houseStatsHeadlineIcon: {
    height: 35,
  },
  stats: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.border.main}`,
    display: 'flex',
    borderRadius: 20,
    flexGrow: 1,
    justifyContent: 'start',
  },
  zestimateMain: {
    color: theme.palette.primary.main,
  },
  subsetText: {
    color: theme.palette.text.secondary,
  },
  statsText: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    flexGrow: 1,
    borderLeft: `1px solid ${theme.palette.border.main}`,
  },
  statsDiv: {
    flexGrow: 1,
    textAlign: 'start',
    display: 'flex',
    justifyContent: 'space-between',
  },
  addManualButton: {
    marginTop: theme.spacing(5),
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'center',
    marginTop: 8,
  },
  selectionRow: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'space-between',
    marginTop: 8,
    gap: 12,
  },
  tooltipPlacement: {
    marginLeft: 6,
    marginTop: -1,
  },
  zillowLegalText: {
    color: theme.palette.text.secondary,
  },
  zillowLegalTextWidthRestriction: {
    color: theme.palette.text.secondary,
  },
  openLink: {
    height: 14,
    width: 14,
    marginLeft: 2,
    marginBottom: 2,
    color: theme.palette.primary.main,
  },
  zillowFooterLegal: {
    flexGrow: 1,
    height: 36,
    display: 'flex',
    flexDirection: 'column',
  },
  houseStatsHeader: {
    flexDirection: 'row',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
  },
  addressText: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  amount: {
    display: 'flex',
    alignItems: 'center',
  },
  flexGrow: {
    flexGrow: 1,
    textAlign: 'center',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingTop: theme.spacing(7),
  },
  manualInputDirective: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingTop: '4px',
  },
  toManualInputButton: {
    padding: '8px 0',
    textTransform: 'none',
  },
  zillowInputDirective: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  toZillowInputButton: {
    padding: '8px 0',
    textTransform: 'none',
  },
  ellipsizeOverflow: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

export type Screens = 'init' | 'LoadingReq' | 'error' | 'ZillowStats' | 'manualAcc';

const getTrackerView = (state: Screens) => {
  switch (state) {
    case 'init':
      return tracker.events.zillowUX_2;
    case 'ZillowStats':
      return tracker.events.zillowUX_4;
    case 'LoadingReq':
    case 'error':
    case 'manualAcc':
    default:
      return tracker.events.zillowUX_3;
  }
};

const PropertyDialog = (props: { linkTo?: string }) => {
  const { linkTo = '0' } = props;
  const allowManual = linkTo === '0';
  const realEstateAccountsById = useAppSelector(accountsSelectors.getRealEstateAccounts);
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const [state, setState] = useState<Screens>('init');
  const [scope] = useState(() => uuidv4().toUpperCase());
  const [accountName, setAccountName] = useState<null | string>(null);
  const [accountNameError, setAccountNameError] = useState<null | string>(null);
  const [amount, setAmountInternal] = useState(Number(0));
  const [accountToLink, setAccountToLink] = useState(linkTo);
  const [failedSubmit, setFailedSubmit] = useState<boolean>(false);
  const privacyModeMask = usePrivacyMaskValue();

  const { isReady: isHouseStatsReady } = useLoadStates(houseStatsSelectors);
  const address: HousingSelectionType = useAppSelector(houseStatsSelectors.getHouse);
  const receivedAllData =
    address && (address.line1 || address.full) && address.assetDetailsList && address.assetDetailsList[0]?.value;

  // for QBP
  const businessEnabled: boolean = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const showBusinessSelect = useAppSelector(
    (state) => businessSelectors.getBusinessList(state)?.size > 0 && businessEnabled,
  );
  const businesses = useAppSelector(businessSelectors.getResourcesById);
  const [businessId, setBusinessId] = useState('PERSONAL');

  const validateName = useCallback(
    (newName: string | null) => {
      if (newName === null || accountToLink !== '0') {
        return null;
      }
      const name = newName.toLowerCase().trim();
      if (name.length === 0) {
        return 'Account name required.';
      }

      if (accountsUtils.isInvalidName(name)) {
        return 'Invalid account name.';
      }

      const accounts = realEstateAccountsById.toList();
      if (accounts.some((account: Account) => account.name?.toLowerCase() === name && accountToLink === '0')) {
        return 'Account name must be unique.';
      }

      return null;
    },
    [accountToLink, realEstateAccountsById],
  );

  useEffect(() => {
    dispatch(
      accountDiscoveryActions.accountDiscoverySetup(accountDiscoveryTypes.mkInstitutionLoginUpserterData(), { scope }),
    );
  }, [dispatch, scope]);

  const updateAccount = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setAccountName(newName);
      setAccountNameError(validateName(newName));
    },
    [validateName],
  );

  const updateAmount = useCallback(
    (e: { target: { value: string } }) => {
      const newAmount = Number(e.target.value);
      setAmountInternal(newAmount);
    },
    [setAmountInternal],
  );

  const updateLink = useCallback(
    (e: ChangeEvent<{ value: string; name: string }>) => {
      setAccountToLink(e.target.value);
      setAccountNameError(e.target.value === '0' ? validateName(accountName) : null);
    },
    [accountName, validateName],
  );

  const refreshData = useCallback(() => {
    setState('init');
  }, []);

  const linkToName = (id: string) => {
    if (id !== '0' && id !== null) {
      const account = realEstateAccountsById.get(id);
      if (!account) {
        return 'Error - Unknown';
      }
      return account.name;
    }
    return 'Add as New (no link)';
  };

  const onClose = useCallback(() => {
    tracker.track(getTrackerView(state), { userAction: 'close' });
    dispatch(rootUiActions.removeDialog(DIALOG_TYPE_ASSET));
  }, [dispatch, state]);

  const handleConfirm = (manualAccount: boolean) => {
    if (isDemoMode() || accountNameError || (manualAccount && !amount)) {
      setFailedSubmit(true);
    } else {
      const submitterObj: ZillowSubmittingObject = {
        manualAccount,
        scope,
        accountNameQCS: accountName || 'Unknown',
        amount,
        link: accountToLink,
        businessId: businessId === 'PERSONAL' ? undefined : businessId,
      };
      dispatch(createZillowAccount(submitterObj));
      onClose();
    }
  };

  const onBack = () => {
    const event = getTrackerView(state);
    switch (state) {
      case 'LoadingReq':
        setState('init');
        tracker.track(event, { userAction: 'loading back' });
        break;
      case 'error':
        setState('init');
        tracker.track(event, { userAction: 'error back' });
        break;
      case 'ZillowStats':
        setState('init');
        setAccountNameError(null);
        setAccountName(null);
        setFailedSubmit(false);
        setAmountInternal(0);
        setAccountToLink(linkTo);
        tracker.track(event, { userAction: 'back' });
        break;
      case 'manualAcc':
        tracker.track(event, { userAction: 'manual back' });
        setAccountNameError(null);
        setAccountName(null);
        setFailedSubmit(false);
        setState('init');
        break;
      default:
        onClose();
        dispatch(accountDiscoveryActions.doAccountDiscovery({ initiator: 'account list dashboard', mode: 'ADD-FI' }));
        break;
    }
  };

  return (
    <StdDialog
      title='Track assets - real estate'
      onClose={onClose}
      size='md'
      onBack={onBack}
      showBackButton
      dialogId={'real-estate-dialog'}
    >
      {state === 'error' ? (
        <ZeroStateView
          button={
            <Button id={'try-again-button'} variant='outlined' onClick={refreshData}>
              TRY AGAIN
            </Button>
          }
          id='failed-to-fetch'
          title='Unable to fetch data'
          description="We can't fetch your data from the server. Please try again"
          qIllustration='planeCrash'
          size='small'
        />
      ) : null}
      {state === 'init' ? (
        <DialogContent className={classes.root}>
          <QIllustration className={classes.headlineIcon} name='goalsHome' size='lg' />
          <div className={classes.headlineRoot}>
            <Typography variant='headlineMedium'>Enter an address</Typography>
            <AddressSearchField
              allowManual={allowManual}
              onChange={(e, value, _reason, _details) => {
                if (value.id === 'Create manually') {
                  tracker.track(tracker.events.zillowUX_3, { status: 'manual account' });
                  setState('manualAcc');
                  setAccountName(value.line1 || '');
                  setAccountNameError(validateName(value.line1 || ''));
                } else {
                  tracker.track(tracker.events.zillowUX_2, { userAction: 'continue' });
                  if (value.assetDetailsList?.[0]?.thirdPartyPropertyID) {
                    setState('LoadingReq');
                    actionHelpers
                      .bindPromiseAction<string, any, { data: HousingSelectionType }>(
                        dispatch,
                        houseStatsActions.getHouseData,
                      )(value.assetDetailsList?.[0].thirdPartyPropertyID)
                      .then((response) => {
                        const accountNameFromQCS = response?.data?.line1 || response?.data?.full || '';
                        setAccountName(accountNameFromQCS);
                        setAccountNameError(validateName(accountNameFromQCS));
                        tracker.track(tracker.events.zillowUX_2, { status: 'success' });
                        setState('ZillowStats');
                      })
                      .catch((e: any) => {
                        tracker.track(tracker.events.zillowUX_3, { status: 'error', statusCode: e.code });
                        setState('error');
                      });
                  } else {
                    tracker.track(tracker.events.zillowUX_3, {
                      status: 'error',
                      statusCode: 'no zillow id (not really possible)',
                    });
                    setState('error');
                  }
                }
              }}
            />
            {allowManual ? (
              <PropertyDialogNavigationButton
                containerClass={classes.manualInputDirective}
                styleClass={classes.toManualInputButton}
                handleDialogNavigation={() => {
                  tracker.track(tracker.events.zillowUX_4, { userAction: 'manual account' });
                  setState('manualAcc');
                }}
                icon={<ArrowForwardIcon />}
                buttonText={`Add manually instead`}
                description={`Add your property’s information to Quicken without connection to Zillow updates.`}
                textVariant={'captionRegular'}
              />
            ) : null}
          </div>
        </DialogContent>
      ) : null}
      {state === 'LoadingReq' ? <LoadingInfo realestate /> : null}
      {state === 'ZillowStats' ? (
        isHouseStatsReady && address ? (
          <>
            <DialogContent className={classes.houseStatsRoot}>
              <div className={classes.houseStatsHeader}>
                <Typography variant='headlineMedium'>Review property value</Typography>
                {receivedAllData && address.assetDetailsList
                  ? ZillowImage(
                      classes.houseStatsHeadlineIcon,
                      tracker.events.zillowUX_4,
                      `https://www.zillow.com/homedetails/${address.assetDetailsList[0].thirdPartyPropertyID}_zpid/`,
                    )
                  : null}
              </div>
              <div className={classes.stats}>
                <div className={classes.statsDiv}>
                  <div className={classes.addressText}>
                    {address.line1 ? (
                      <>
                        <Typography variant='bodyRegular'>{getPropertyNameFirstPart(address)}</Typography>
                        <Typography variant='bodyRegular'>{getPropertyNameSecondPart(address)}</Typography>
                      </>
                    ) : (
                      <Typography variant='bodyRegular'>{address.full || 'Unknown'}</Typography>
                    )}
                    {receivedAllData && address.assetDetailsList ? (
                      <Typography variant='captionRegular' className={classes.subsetText}>
                        {describeProperty(address.assetDetailsList[0])}
                      </Typography>
                    ) : null}
                  </div>
                  <div className={classes.statsText}>
                    <Typography variant='titleMedium' className={classes.zestimateMain}>
                      Zestimate:
                    </Typography>
                    <div className={classes.amount}>
                      <Typography variant='headlineMedium'>
                        {' '}
                        {address.assetDetailsList?.[0]?.value
                          ? `$${privacyModeMask(address.assetDetailsList[0].value.toLocaleString())}`
                          : 'Unknown'}
                      </Typography>
                      <InfoTooltipIcon
                        className={classes.tooltipPlacement}
                        tooltipPlacement='right'
                        tooltipText='The zestimate is an estimate of the value of the property selected as determined by Zillow. Zestimate® home valuation | Provided via the Zestimate API'
                        size='small-no-padding'
                      />
                    </div>
                  </div>
                </div>
              </div>
              {receivedAllData && address.assetDetailsList ? (
                <>
                  <div className={classes.flexRow}>
                    <Link
                      href={`https://www.zillow.com/homedetails/${address.assetDetailsList[0].thirdPartyPropertyID}_zpid/`}
                      underline='none'
                      target='_blank'
                      rel='noopener noreferrer'
                      variant='captionRegular'
                      className={classes.zillowLegalTextWidthRestriction}
                    >
                      <Typography variant='captionRegular'>
                        See more details for{' '}
                        {address.line1 ? getPropertyNameFirstPart(address) : address.full || 'Unknown'} on Zillow
                        <OpenInNewIcon className={classes.openLink} />
                      </Typography>
                    </Link>
                  </div>
                  <div className={classes.divider}>
                    <Typography variant='titleMedium'>Confirm details</Typography>
                    <Divider className={classes.flexGrow} />
                  </div>
                  <div className={classes.selectionRow}>
                    <TextField
                      name='accountName'
                      label='Account Name'
                      inputProps={{
                        id: 'accountName',
                      }}
                      disabled={accountToLink !== '0'}
                      value={accountToLink !== '0' ? linkToName(accountToLink) : accountName}
                      onChange={updateAccount}
                      InputProps={{
                        autoComplete: 'off',
                        inputProps: {
                          maxLength: 64,
                        },
                      }}
                      margin='none'
                      fullWidth
                      error={failedSubmit && !!accountNameError}
                      helperText={failedSubmit && accountNameError}
                    />
                    {showBusinessSelect && (
                      <UsageSelect
                        value={businessId}
                        onChange={(_e: any, newValue: string) => setBusinessId(newValue)}
                        showPersonal
                        iconOnly={false}
                        sx={{
                          width: '100%',
                          height: '100%',
                        }}
                        id='usage-select'
                        hiddenLabel={false}
                        label='Primary Usage'
                        margin='none'
                        slotProps={{
                          select: {
                            renderValue: () => (
                              <Box display='flex' height='40px' alignItems='flex-end'>
                                <Typography className={classes.ellipsizeOverflow}>
                                  {businesses.get(businessId)?.name ?? 'Personal'}
                                </Typography>
                              </Box>
                            ),
                          },
                        }}
                      />
                    )}
                    {realEstateAccountsById.size !== 0 && (
                      <AccountLink value={accountToLink} onChange={updateLink} options={realEstateAccountsById} />
                    )}
                  </div>
                </>
              ) : allowManual ? (
                <div className={classes.flexGrow}>
                  <Button
                    variant='outlined'
                    id='add-manually-btn'
                    className={classes.addManualButton}
                    onClick={() => {
                      tracker.track(tracker.events.zillowUX_4, { userAction: 'manual account' });
                      setState('manualAcc');
                    }}
                  >
                    Add Manually
                  </Button>
                </div>
              ) : null}
              {/* <Typography variant='captionRegular'  className={classes.autoUpdate}>
               Once real estate is added, this value will automatically update every 30 days.
              </Typography> */}
            </DialogContent>
            {receivedAllData ? (
              <StdDialogActions
                primary={{
                  children: 'Confirm',
                  onClick: () => handleConfirm(false),
                  showTooltip: failedSubmit && (isDemoMode() || !!accountNameError),
                  ...(isDemoMode() && { tooltipText: 'Not available in Test Drive.' }),
                }}
                tertiaryNode={
                  <div className={classes.zillowFooterLegal}>
                    {ZillowTerms(classes.zillowLegalText, tracker.events.zillowUX_4)}
                    <Typography variant='captionRegular' className={classes.zillowLegalText}>
                      Disclaimer: data is provided as-is via the Zestimate API
                    </Typography>
                  </div>
                }
              />
            ) : null}
          </>
        ) : (
          <Box className={classes.errorTab}>
            <ZeroStateView // in case the zillow request fails and we dont end up in error state
              title='Unable to fetch data'
              description="We can't fetch your data from the server. Please try again"
              button={
                <Button id={'try-again-button'} variant='outlined' onClick={refreshData}>
                  TRY AGAIN
                </Button>
              }
              id='failed-to-fetch'
              qIllustration='planeCrash'
              size='small'
            />
          </Box>
        )
      ) : null}
      {state === 'manualAcc' ? (
        <>
          <DialogContent className={classes.root}>
            <Typography variant='titleMedium'>Enter property details</Typography>
            <TextField
              label='Account Name'
              inputProps={{
                id: 'accountName',
              }}
              className={classes.accountName}
              name='accountName'
              value={accountName}
              onChange={updateAccount}
              InputProps={{
                autoComplete: 'off',
                inputProps: {
                  maxLength: 32,
                },
              }}
              margin='normal'
              fullWidth
              error={failedSubmit && !!accountNameError}
              helperText={failedSubmit && accountNameError}
            />
            <QAmountField
              editable
              noNegatives
              value={amount}
              onChange={updateAmount}
              id='schedtxn-amount'
              label='Estimated Value'
              autoComplete='off'
              InputProps={{ id: 'manual_ending_balance' }}
              error={failedSubmit && !amount}
              helperText={failedSubmit && !amount && 'Amount required'}
              className={classes.estimatedValue}
            />
            <PropertyDialogNavigationButton
              containerClass={classes.zillowInputDirective}
              styleClass={classes.toZillowInputButton}
              handleDialogNavigation={() => {
                tracker.track(tracker.events.zillowUX_2, { userAccount: 'init' });
                setState('init');
              }}
              icon={<ArrowForwardIcon />}
              buttonText={'Add with Zillow instead'}
              description={'Link your property to its Zillow value.'}
              textVariant={'captionRegular'}
            />
          </DialogContent>
          <StdDialogActions
            primary={{
              children: 'Confirm',
              onClick: () => handleConfirm(true),
              showTooltip: failedSubmit && (!amount || !!accountNameError),
            }}
          />
        </>
      ) : null}
    </StdDialog>
  );
};

export default PropertyDialog;
